import React, { useMemo } from 'react';

import { isFundRaising, isPaid, isRefunded, OwnedInvestment as MyInvestment } from 'api-client';
import { DateTimeline, MetricProgressBar, Money, TimelinePoint } from 'components/common';
import { formatPercents } from 'formatters';

const calculatePercentage = (current: number, total: number): number => {
  if (total <= 0) {
    return 0;
  }
  return (current / total) * 100;
};

export const useMyInvestmentContent = (investment: MyInvestment) => {
  const project = investment.project;

  const roiIrr = useMemo(() => {
    if (isPaid(project.status)) {
      return `${formatPercents(project.factROI ?? 0, 0)} / ${formatPercents(project.factInterestRate ?? 0, 0)}`;
    }

    return `${formatPercents(project.simpleInterestRate, 0)} / ${formatPercents(project.interestRate, 0)}`;
  }, [project]);

  const progressBar = useMemo(() => {
    if (isFundRaising(project.status)) {
      return (
        <MetricProgressBar
          currentValue={<Money money={project.investedSum} compact />}
          totalValue={<Money money={project.debtPrice} compact />}
          currentLabel="Собрано"
          totalLabel="Сумма сбора"
          percentage={calculatePercentage(project.investedSum.amount, project.debtPrice.amount)}
        />
      );
    }

    const forecastAmount = investment.recoveryForecast?.amount ?? 0;
    const collectedAmount = investment.actualPayment?.amount ?? 0;

    return (
      <MetricProgressBar
        currentValue={
          investment.actualPayment ? <Money money={investment.actualPayment} compact /> : '—'
        }
        totalValue={
          investment.recoveryForecast ? <Money money={investment.recoveryForecast} compact /> : '—'
        }
        currentLabel="Взыскано"
        totalLabel="Прогноз взыскания"
        percentage={calculatePercentage(collectedAmount, forecastAmount)}
      />
    );
  }, [investment, project]);

  const timeline = useMemo(() => {
    const fundRaising = isFundRaising(project.status);
    const finished = isPaid(project.status) || isRefunded(project.status);

    let endDate = project.projectEndDate ?? null;
    if (isPaid(project.status)) {
      endDate = project.paidAt ?? project.projectEndDate ?? null;
    }
    if (isRefunded(project.status)) {
      endDate = project.cancelledAt ?? project.projectEndDate ?? null;
    }

    const points: Array<TimelinePoint> = [
      { date: project.maturityDate, label: 'Сбор до', reached: true },
      { date: project.loanIssuedAtDate, label: 'Старт проекта', reached: !fundRaising },
      { date: endDate, label: 'Конец проекта', reached: finished },
    ];

    return <DateTimeline points={points} />;
  }, [project]);

  return { roiIrr, progressBar, timeline };
};
